/* eslint-disable import/no-extraneous-dependencies */
import { useSortable } from '@dnd-kit/sortable'
import { CSS as css } from '@dnd-kit/utilities'
import {
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import EditIcon from '@material-ui/icons/Create'
import DeleteIcon from '@material-ui/icons/Delete'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace'
import SwapVertIcon from '@material-ui/icons/SwapVert'

import { addCommaToPrice } from '../../helpers/numbers'
import { persianRegex } from '../../helpers/regex'
import { getTranslate, lang } from '../../localization'
import { Currency } from '../../types/currency'
import {
  actionTypes,
  PriceAction,
  PriceActionWithEditMode
} from '../../types/priceClass'
import { recalcPriceActionErrors } from './priceActionChain'
import useStyles from './styles/PriceActionRow.styles'

interface PriceActionRowProps {
  priceAction: PriceAction
  setPriceActionWithEditMode: React.Dispatch<
    React.SetStateAction<PriceActionWithEditMode[]>
  >
  priceActionWithEditMode: PriceActionWithEditMode[]
  index: number
  id: string
  archivedAt: string | undefined
  currencies: {
    input: Currency
    output: Currency
  }
}

const PriceActionRow: React.FC<PriceActionRowProps> = ({
  priceAction,
  setPriceActionWithEditMode,
  priceActionWithEditMode,
  index,
  id,
  archivedAt,
  currencies
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('xs'))
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: id })
  const style = {
    transform: css.Transform.toString(transform),
    transition
  }

  const deleteObject = () => {
    const updatedList = [
      ...priceActionWithEditMode.slice(0, index),
      ...priceActionWithEditMode.slice(index + 1)
    ]

    const errors = recalcPriceActionErrors(
      updatedList,
      currencies.input,
      currencies.output
    )
    const recalculatedList = updatedList.map((item, idx) => ({
      ...item,
      error: errors[idx]
    }))

    setPriceActionWithEditMode(recalculatedList)
  }

  const editObject = () => {
    if (priceActionWithEditMode && priceActionWithEditMode?.length > 0) {
      setPriceActionWithEditMode([
        ...priceActionWithEditMode?.slice(0, index),
        {
          ...priceActionWithEditMode[index],
          editing: true
        },
        ...priceActionWithEditMode?.slice(index + 1)
      ])
    }
  }

  const isOpenAnyPriceAction =
    priceActionWithEditMode.length > 0 &&
    priceActionWithEditMode.every((item) => !item.editing)

  const ArrowDownward = () => {
    return <KeyboardBackspaceIcon className={classes.arrowDownward} />
  }
  const ArrowUpward = () => {
    return <KeyboardBackspaceIcon className={classes.arrowUpward} />
  }

  const isPersian = persianRegex.test(priceAction.beneficiary?.entityName || '')
  const isDisplayRate = priceAction.actionType === 'DisplayRate'
  const direction = isDisplayRate
    ? priceAction.actionAmount.direction || 'Decrease'
    : undefined

  const actionType = actionTypes.find(
    (item) => item.value === priceAction.actionType
  )?.label

  const rowBody = (
    <>
      {isOpenAnyPriceAction && priceActionWithEditMode.length > 1 && (
        <IconButton
          {...listeners}
          className={[
            classes.arrowIconContainer,
            isDisplayRate ? classes.arrowIconContainerSink : ''
          ].join(' ')}>
          {index === 0 ? (
            <ArrowDownward />
          ) : index === priceActionWithEditMode.length - 1 ? (
            <ArrowUpward />
          ) : (
            <SwapVertIcon className={classes.swapVertIcon} />
          )}
        </IconButton>
      )}
      <div
        className={[
          classes.priceActionContainer,
          priceActionWithEditMode[index].error &&
            classes.priceActionContainerWithError
        ].join(' ')}>
        <div
          className={[
            classes.titleContainer,
            classes.titleContainerMargin
          ].join(' ')}>
          <Typography variant="caption" className={classes.title}>
            {getTranslate('Action Type')}
          </Typography>
          <Typography variant="body1" className={classes.subTitle}>
            {(priceAction.actionAmount.amount
              ? addCommaToPrice(priceAction.actionAmount.amount) +
                (priceAction.actionAmount.type === 'FixedFee'
                  ? ` ${getTranslate(
                      priceAction.actionAmount.toCurrency || ''
                    )} `
                  : '% ')
              : '') + getTranslate(actionType || '')}
            {direction ? ` (${getTranslate(direction)})` : ''}
            {isDisplayRate ? (
              <span className={classes.typeBadge}>
                {getTranslate('Capture')}
                <Tooltip
                  title={getTranslate(
                    'A Capture action reads a value from the rate flow without changing it — the running net rate passes through unchanged.'
                  )}>
                  <span style={{ display: 'inline-flex' }}>
                    <InfoOutlinedIcon className={classes.typeInfoIcon} />
                  </span>
                </Tooltip>
              </span>
            ) : (
              <span className={classes.typeBadge}>
                {getTranslate('Transform')}
                <Tooltip
                  title={getTranslate(
                    'A Transform action changes the running rate and passes the result on to the next step.'
                  )}>
                  <span style={{ display: 'inline-flex' }}>
                    <InfoOutlinedIcon className={classes.typeInfoIcon} />
                  </span>
                </Tooltip>
              </span>
            )}
          </Typography>
        </div>
        {!isMobileScreen && (
          <div className={classes.titleContainer}>
            <Typography variant="caption" className={classes.title}>
              {getTranslate('Beneficiary')}
            </Typography>
            <Typography
              variant="body1"
              className={classes.subTitle}
              style={isPersian ? { fontFamily: 'IranSans' } : undefined}>
              {priceAction.beneficiary?.entityName || '--'}
            </Typography>
          </div>
        )}
        <div className={classes.titleContainer}>
          <Typography variant="caption" className={classes.title}>
            {getTranslate('Input → Output Currency')}
          </Typography>
          <Typography variant="body1" className={classes.subTitle}>
            {lang === 'en'
              ? `${priceAction.actionAmount.fromCurrency} ---> ${priceAction.actionAmount.toCurrency}`
              : `${priceAction.actionAmount.toCurrency} <--- ${priceAction.actionAmount.fromCurrency}`}
          </Typography>
        </div>
        <div className={classes.buttonsContainer}>
          <IconButton
            className={classes.editIconContainer}
            disabled={!!archivedAt}
            onClick={(e) => {
              e.stopPropagation()
              editObject()
            }}>
            <EditIcon />
          </IconButton>
          <IconButton
            className={classes.deleteIconContainer}
            disabled={!!archivedAt}
            onClick={(e) => {
              e.stopPropagation()
              deleteObject()
            }}>
            <DeleteIcon />
          </IconButton>
        </div>
        {isMobileScreen && (
          <div className={classes.titleContainer}>
            <Typography variant="caption" className={classes.title}>
              {getTranslate('Beneficiary')}
            </Typography>
            <Typography
              variant="body1"
              className={classes.subTitle}
              style={isPersian ? { fontFamily: 'IranSans' } : undefined}>
              {priceAction.beneficiary?.entityName}
            </Typography>
          </div>
        )}
      </div>
    </>
  )

  return (
    <div
      style={style}
      {...attributes}
      ref={setNodeRef}
      className={
        isDisplayRate
          ? classes.tapRow
          : isOpenAnyPriceAction && priceActionWithEditMode.length > 1
          ? classes.root
          : ''
      }>
      {isDisplayRate ? (
        <>
          <span className={classes.tapConnector} />
          <div className={classes.tapContent}>{rowBody}</div>
        </>
      ) : (
        rowBody
      )}
    </div>
  )
}

export default PriceActionRow
